import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { referralService } from "@/services/referralService";

export interface UseReferralCodeOptions {
  /** When false, skips the API call (e.g. profile modal closed). Defaults to true on eligible routes. */
  enabled?: boolean;
}

/**
 * Hook to manage user's referral code.
 * Skips fetching on admin routes to avoid timeouts and unnecessary API load.
 */
export function useReferralCode(options?: UseReferralCodeOptions) {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const isAdminRoute = pathname.startsWith("/admin");
  const isCompanyPortal = pathname.startsWith("/c/");
  const routeEnabled = options?.enabled ?? true;

  // Fetch user's referral code (disabled on admin and company portal - company users don't need it)
  const {
    data: referralCode,
    isLoading,
    error: _error,
  } = useQuery({
    queryKey: ["referralCode"],
    enabled: routeEnabled && !isAdminRoute && !isCompanyPortal,
    queryFn: async () => {
      try {
        const data = await referralService.getReferralCode();
        // Return null instead of throwing - 404 is a valid state (user has no referral code yet)
        return data || null;
      } catch (error) {
        // If it's a 404 or "not found" error, return null instead of throwing
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (
          errorMessage.includes("404") ||
          errorMessage.includes("not found") ||
          errorMessage.includes("Not Found")
        ) {
          return null;
        }
        // Re-throw other errors
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404 errors (user simply doesn't have a referral code yet)
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (
        errorMessage.includes("404") ||
        errorMessage.includes("not found") ||
        errorMessage.includes("Not Found")
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Update custom code
  const updateCustomCode = useMutation({
    mutationFn: (newCustomCode: string) => {
      // Validate format
      if (!/^[A-Za-z0-9_-]{6,20}$/.test(newCustomCode)) {
        throw new Error("Code must be 6-20 characters (letters, numbers, underscore, hyphen only)");
      }

      // Check if can update (90 days limit)
      if (referralCode?.last_customized_at) {
        const lastUpdate = new Date(referralCode.last_customized_at);
        const daysSinceUpdate = (Date.now() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceUpdate < 90) {
          const daysRemaining = Math.ceil(90 - daysSinceUpdate);
          throw new Error(`You can change your code in ${daysRemaining} days`);
        }
      }

      return referralService.updateCustomCode(newCustomCode);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["referralCode"] });
      toast.success("Referral code updated successfully!");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update referral code");
    },
  });

  // Get referral link
  const getReferralLink = (code?: string) => {
    const activeCode = code || referralCode?.custom_code || referralCode?.code;
    if (!activeCode) return "";
    return `${window.location.origin}/r/${activeCode}`;
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    const link = getReferralLink();
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Referral link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy referral link:", error);
      toast.error("Failed to copy link");
    }
  };

  // Share via platform
  /**
   * Share Via.
   */
  const shareVia = (platform: "whatsapp" | "email" | "copy") => {
    const link = getReferralLink();
    const message = `Join me on TellerWand to track your finances! Sign up with my link and get 1 month Pro free: ${link}`;

    switch (platform) {
      case "whatsapp":
        window.open(
          `https://wa.me/?text=${encodeURIComponent(message)}`,
          "_blank",
          "noopener,noreferrer"
        );
        break;
      case "email":
        window.location.href = `mailto:?subject=Join TellerWand&body=${encodeURIComponent(message)}`;
        break;
      case "copy":
        copyToClipboard();
        break;
    }
  };

  return {
    referralCode,
    isLoading,
    error: _error,
    updateCustomCode: updateCustomCode.mutate,
    isUpdating: updateCustomCode.isPending,
    getReferralLink,
    copyToClipboard,
    shareVia,
  };
}
